import type { Context, Schema } from 'effect';
import { Effect } from 'effect';
import * as HttpClientResponse from 'effect/unstable/http/HttpClientResponse';

import { schemaBodyJsonClean } from '@vs-shop/schemas/transform/schemaBodyJsonClean';

import type { createBackendApiError } from '../../data/error/backendApi/_internal/createBackendApiError';
import { addResponseStatusToSpan } from '../../utils/httpTracing';
import { logApiOperation } from '../../utils/logger/apiLogger';
import type { ApiModule } from '../logger/ApiModule';
import type { ApiRequestIdentifier } from './apiOperation';
import { identifierFields } from './apiOperation';
import type { OpenApiClient } from './OpenApiClient';
import type {
  AvailableMethods,
  ExtractContentTypes,
  ExtractPathParams,
  ExtractQueryParams,
  RequestOptions,
} from './openApiTypes';

/** The error family one operation reports, as `createBackendApiError` builds it. */
type ApiRequestErrors = ReturnType<typeof createBackendApiError>;

/**
 * The engine's request template: every read operation a tenant serves, over
 * that tenant's generated OpenAPI client.
 *
 * A tenant binds the template to its client once —
 * `const request = makeApiRequest(RacinglineBackendApi)` — and each operation
 * after that is one `request` call naming its endpoint, its schema and its
 * error names, plus the line that supplies the parameters:
 *
 * ```ts
 * const taxonByCode = request({
 *   path: '/api/v2/shop/taxons/{code}/expand',
 *   method: 'get',
 *   accept: 'application/ld+json',
 *   schema: TaxonExpanded,
 *   errors: GetTaxonByCodeError,
 *   module: ApiModule.CategoryGet,
 * });
 *
 * export const getTaxonByCode = ({ code }: GetTaxonByCodeInput) =>
 *   taxonByCode({ params: { code: rawUrlEncode(code) } }, code);
 * ```
 *
 * The split is the operation's own shape: the endpoint, the schema, the error
 * family and the log module are fixed for the life of the operation, and only
 * the parameters and the identifier vary per call.
 *
 * What the template supplies is the part every hand-written request module
 * copied and then diverged on: the `http.<method> <path>` client span, the
 * `ValidateResponse` span around the decode, the operation span, and the
 * `logApiOperation` line. `module` names both the operation span and the log
 * module, because an {@link ApiModule} value IS the `api.<domain>.<op>` string
 * the span wants.
 *
 * It composes the engine pieces that already exist — `schemaBodyJsonClean`,
 * `addResponseStatusToSpan`, `logApiOperation`, `createBackendApiError` — and
 * adds no second span or logger implementation of its own.
 *
 * Status handling is the one every module it replaces shared: 404 becomes the
 * family's `NotFoundError`, every other non-2xx its `ApiRequestError`, and a
 * 2xx body decodes through the operation's schema with JSON-LD metadata and
 * the generated rest records stripped.
 *
 * The template covers reads. `accept` is the only content negotiation it
 * performs, and the client requires a second argument from an operation that
 * sends a request body, so a write cannot run through this function.
 * `makeApiWrite` and `makeApiCommand` are the write side, and they share this
 * template's spans, log line and status vocabulary.
 */
export const makeApiRequest =
  <TClient, TPaths>(client: Context.Key<TClient, OpenApiClient<TPaths>>) =>
  <
    TPath extends keyof TPaths & string,
    TMethod extends AvailableMethods<TPaths[TPath]>,
    TAccept extends ExtractContentTypes<TPaths[TPath][TMethod]>,
    TSchema extends Schema.Top,
    TErrors extends ApiRequestErrors,
  >(operation: {
    readonly path: TPath;
    readonly method: TMethod;
    readonly accept: TAccept;
    readonly schema: TSchema;
    readonly errors: TErrors;
    readonly module: ApiModule;
  }) =>
  (
    options: RequestOptions<
      ExtractPathParams<TPaths[TPath][TMethod]>,
      ExtractQueryParams<TPaths[TPath][TMethod]>
    >,
    identifier?: ApiRequestIdentifier
  ) => {
    const identity = identifierFields(operation.errors.identifierField, identifier);

    const fetched = Effect.gen(function* () {
      const api = yield* client;

      return yield* api
        .path(operation.path)
        .method(operation.method)
        .accept(operation.accept)
        .fetch(options);
    }).pipe(
      Effect.map((fetchResult) => fetchResult.response),
      Effect.tap(addResponseStatusToSpan),
      Effect.withSpan(`http.${operation.method} ${operation.path}`, {
        kind: 'client',
        attributes: { 'http.request.method': operation.method, ...identity },
      })
    );

    return fetched.pipe(
      Effect.flatMap(
        HttpClientResponse.matchStatus({
          '2xx': (response) => schemaBodyJsonClean(operation.schema)(response),
          404: (response) =>
            Effect.fail(new operation.errors.NotFoundError({ ...identity, response })),
          orElse: (response) =>
            Effect.fail(new operation.errors.ApiRequestError({ ...identity, response })),
        })
      ),
      Effect.withSpan('ValidateResponse'),
      Effect.tap(() =>
        logApiOperation(
          operation.module,
          'Debug',
          `${operation.module} resolved ${operation.errors.identifierField}: ${String(identifier ?? operation.path)}`
        )
      ),
      Effect.withSpan(operation.module, { kind: 'internal', attributes: identity })
    );
  };
